<template>
	<div class="element-signature form-group is-empty" :data-element="element.id">
		<label v-if="isSigned || !showDate" class="element-label" :class="classes('title')"
			:title="required ? trans('buttons.required') : ''">
			<span v-html="macro(label)"></span>
			<small v-if="required" class="required">*</small>
		</label>
		<div v-if="!isSigned && showDate" class="row">
			<label class="element-label col-sm-4" :class="classes('title')"><span>{{ $t('app.common.date') }}</span></label>
			<label class="element-label col-sm-8" :class="classes('title')"><span v-html="macro(label)"></span></label>
		</div>
		<div v-if="!isSigned && isPrinting" class="row">
			<div v-if="showDate" class="col-sm-4 pl-0">
				<div class="form-control" :class="classes('input')">&nbsp;</div>
			</div>
			<div :class="showDate ? 'col-sm-8 pl-0' : 'col-sm-12 pl-0'">
				<div class="form-control" :class="classes('input')">&nbsp;</div>
			</div>
		</div>
		<div class="position-relative">
			<div v-if="!isOpened" class="signed input-group" :class="{ 'is-invalid': !isValid }">
				<div v-if="!isSigned && !isPrinting && !canSign && !canAllow" class="form-control cursor-not-allowed"
					tabindex="0" :class="classes('input')" :title="trans('documents.not_allowed')"><span
						class="text-muted font-italic">&nbsp;</span></div>
				<div v-if="!isSigned && !isPrinting && !canSign && canAllow" class="form-control cursor-pointer"
					tabindex="0" :class="classes('input')" @click="toggle('allow')" @keydown.space="toggle('allow')"
					@keydown.enter="toggle('allow')"><span class="text-muted font-italic">{{ trans('documents.sign')
					}}</span>
				</div>
				<div v-if="!isSigned && !isPrinting && !canSign && canAllow" class="input-group-text cursor-pointer"
					@click="toggle('allow')" :class="classes('input')">
					<app-icon value="pen"></app-icon>
				</div>
				<div v-if="!isSigned && !isPrinting && canSign" ref="sigEl" class="form-control cursor-pointer" tabindex="0"
					:class="classes('input')" @click="toggle(canType ? 'type' : 'draw')"
					@keydown.space="toggle(canType ? 'type' : 'draw')" @keydown.enter="toggle(canType ? 'type' : 'draw')">
					<span class="text-muted font-italic">{{ trans('documents.sign') }}</span>
				</div>
				<div v-if="!isSigned && !isPrinting && canSign" class="input-group-text cursor-pointer"
					@click="toggle(canType ? 'type' : 'draw')" :class="classes('input')">
					<app-icon value="pen"></app-icon>
				</div>
				<div v-if="isSigned" class="form-control draw" :class="classes('input')">
					<img class="signature" :src="signature.value" />
				</div>
				<div class="input-group-text cursor-pointer" v-if="isSigned && canUnsign && !readonly" tabindex="0"
					@click="remove" :class="classes('input')" @keydown.space="remove" @keydown.enter="remove"
					:title="$t('app.common.remove')">
					<app-icon value="close"></app-icon>
				</div>
			</div>
			<div v-if="isOpened" class="signature-container">
				<ul v-if="!active.allow" class="nav nav-tabs">
					<li class="nav-item" v-if="canType">
						<a href="javascript:void(0);" @click="toggle('type')" class="nav-link"
							:class="{ 'active': active.type }">
							{{ trans('buttons.type') }}
						</a>
					</li>
					<li class="nav-item" v-if="canDraw">
						<a href="javascript:void(0);" @click="toggle('draw')" class="nav-link"
							:class="{ 'active': active.draw }">
							{{ trans('buttons.draw') }}
						</a>
					</li>
				</ul>
				<div v-if="active.allow" class="content allow">
					<p class="header">{{ trans('documents.signin') }} {{ allowed.name }}</p>
					<p class="description">{{ trans('documents.allowed') }}
						{{ allowed.name }}.<br /></p>
				</div>
				<div v-if="!active.allow" v-show="canType && active.type" class="content typed">
					<input type="text" class="form-control form-control-lg" ref="ityped" required maxlength="64"
						v-model="typed">
					<canvas ref="typed" class="invisible position-absolute"></canvas>
				</div>
				<div v-if="!active.allow" v-show="canDraw && active.draw" class="content drawed">
					<button type="button" class="btn" :title="$t('app.common.clear_all')" @click="clear">
						<app-icon value="close"></app-icon>
					</button>
					<canvas ref="drawed"
						style="touch-action: none; max-width:100% !important; max-height:100% !important;"></canvas>
				</div>
				<div class="actions">
					<button v-if="!active.allow" type="button" class="app--button primary" @click="save"
						:disabled="isEmpty">
						{{ trans('buttons.save') }}
					</button>
					<button v-if="active.allow" type="button" class="app--button primary" @click="auth">
						{{ trans('buttons.signin') }}
					</button>
					<button type="button" class="app--button muted" @click="cancel">
						{{ trans('buttons.cancel') }}
					</button>
				</div>
			</div>
			<input v-show="!isPrinting" type="text" :required="required" :value="isSigned ? 'true' : null"
				class="input-signature" ref="validSig" />
			<div class="invalid-feedback" :class="{ 'd-block': !isValid }">
				<span v-if="!isValid">{{ $t('validation.mandatory') }}</span>
			</div>
		</div>
		<small class="form-text" :class="classes('description')">
			<span v-if="isSigned && options('scope') != 'NONE'">
				<span>{{ trans('documents.signed') }}</span>
				<span :title="signature.email">&nbsp;{{ signature.name }}&nbsp;</span>
				<span>{{ trans('buttons.on') }}</span>
				<span>&nbsp;{{ signed_at }}&nbsp;</span>
			</span>&nbsp;
		</small>
	</div>
</template>

<script>
import Pad from 'signature_pad'
import store from '../../store'

export default {
	props: {
		definition: { type: Object, required: true },
		element: { type: Object, required: true },
	},
	computed: {
		id() { return store.state.id + '|' + this.element.panel + '|' + this.element.group + '|' + this.definition.id },
		required() {
			if (this.options('required') != true) {
				return false
			}
			return this.canSign
		},
		label() { return this.options('label') },
		signature() { return store.state.signatures.find(s => s.id == this.id) },
		signed_at() {
			if (!this.isSigned || !this.signature.signed_at) {
				return null
			}
			let v = this.signature.signed_at
			v = typeof v == 'object' && v.hasOwnProperty('date') ? v.date : v
			return new Date(v.substring(0, 19).replace(" ", "T") + 'Z').toUTCString()
		},
		allowed() {
			let member = null
			let panel = store.state.panels.find(p => p.id == this.element.panel)
			if (panel && panel.type != 'ACCOUNT') {
				member = store.state.members.find(m => m.type == panel.type)
			} else if (this.options('allow') == 'CLIENT') {
				member = store.state.members.find(m => m.coclient == false && m.type != 'CONTACT')
			} else if (this.options('allow') == 'COCLIENT') {
				if (!this.hasEmptySignerAssigned && !this.options('group')) {
					member = store.state.members.find(m => m.coclient == true && m.type != 'CONTACT')
				} else {
					member = store.state.members.find(m => m.signer == (this.options('group') ?? 'Coclient'))
				}
			}
			return member && member.email != null ? member : ''
		},
		isEmpty() { return !this.typed && this.emptyPad },
		isLoading() { return store.state.loading == true },
		isOpened() { return this.active.type == true || this.active.draw == true || this.active.allow == true },
		isPreviewing() { return store.state.preview == true },
		isPrinting() { return store.state.printing == true },
		isSigned() { return this.signature && this.signature.value != null },
		isValid() { return !(this.element && this.element.valid === false) },
		hasEmptySignerAssigned() { return store.state.members.filter(m => m.signer == 'Coclient').length > 0 },
		canDraw() { return !this.options('input') || this.options('input') == 'DRAW' },
		canType() { return !this.options('input') || this.options('input') == 'TYPE' },
		canUnsign() {
			if (store.state.complete == true) { return false }
			if (this.isPreviewing && store.state.complete == false) { return true }
			var panel = store.state.panels.find(p => p.id == this.element.panel)
			var member = store.state.members.find(m => m.active == true)
			if (!member) { return false }
			if (panel && panel.type != 'ACCOUNT') {
				return member && member.type != 'CONTACT' && panel.type == member.type && member.email != null
			}
			if (this.options('allow') == 'CLIENT') {
				return member.coclient == false && member.email != null && member.type != 'CONTACT'
			}
			if (this.options('allow') == 'COCLIENT') {
				if (!this.hasEmptySignerAssigned && !this.options('group')) {
					return member.coclient == true && member.email != null
				}
				return member.signer == (this.options('group') ?? 'Coclient') && member.email != null
			}
			return panel.type == 'ACCOUNT'
		},
		canSign() {
			if (this.isPreviewing) { return false }
			var panel = store.state.panels.find(p => p.id == this.element.panel)
			var member = store.state.members.find(m => m.active == true)
			if (!member) { return false }
			//In client/co-client pages, only allow signing if the logged in member is of the same type as the page 
			if (panel && panel.type != 'ACCOUNT') {
				return member && member.type != 'CONTACT' && panel.type == member.type && member.email != null
			}
			//In signatures that allow the primary contact, find if the logged in member is the primary contact
			if (this.options('allow') == 'CLIENT') {
				return member.coclient == false && member.email != null && member.type != 'CONTACT'
			}
			//In signatures that allow the secondary contact, find if the logged in member matches the group or is the coclient
			if (this.options('allow') == 'COCLIENT') {
				if (!this.hasEmptySignerAssigned && !this.options('group')) {
					return member.coclient == true && member.email != null
				}
				return member.signer == (this.options('group') ?? 'Coclient') && member.email != null
			}
			return panel.type == 'ACCOUNT'
		},
		canAllow() {
			if (this.canSign || this.isPreviewing || store.state.type != 'engagement') {
				return false
			}
			var panel = store.state.panels.find(p => p.id == this.element.panel)
			//In client/coclient pages, find the member from the same page type and only allow if that member is not currently logged in
			if (panel && panel.type != 'ACCOUNT') {
				var member = store.state.members.find(m => m.type == panel.type)
				return member && member.email != null && !member.active
			}
			//In household pages, find the primary contact member and only allow if that member is not currently logged in
			if (this.options('allow') == 'CLIENT') {
				var member = store.state.members.find(m => m.coclient == false && m.type != 'CONTACT')
				return member && member.email != null && !member.active
			}
			//In household pages, fint the contact per group and only allow if that member is not currently logged in
			if (this.options('allow') == 'COCLIENT') {
				if (!this.hasEmptySignerAssigned && !this.options('group')) {
					var member = store.state.members.find(m => m.coclient == true && m.type != 'CONTACT')
				} else {
					var member = store.state.members.find(m => m.signer == (this.options('group') ?? 'Coclient'))
				}
				return member && member.email != null && !member.active
			}
			return false
		},
		showDate() { return this.isPrinting && this.options('scope') != 'NONE' },
		readonly() { return store.getters.readonly(this.definition, this.isSigned) },
	},
	data() {
		return { typed: null, pad: null, emptyPad: true, active: { type: false, draw: false, allow: false }, loading: false }
	},
	created() {
		if (!this.signature) {
			store.commit('ADD_SIGNATURE', {
				id: this.id,
				panel: this.element.panel,
				group: this.element.group,
				definition: this.definition.id,
			})
		}
	},
	mounted() {
		if (this.required) {
			let vm = this
			this.$refs.validSig.addEventListener('invalid', function () {
				if (vm.$refs.sigEl) vm.$refs.sigEl.classList.add('is-invalid')
			}, false);
			this.$refs.validSig.addEventListener('valid', function () {
				if (vm.$refs.sigEl) vm.$refs.sigEl.classList.remove('is-invalid')
			}, false);
		}
	},
	watch: {
		isOpened(value) {
			this.$nextTick(() => {
				if (!this.active.allow) {
					if (value && this.pad == null) {
						let vm = this
						let container = this.$el.querySelector(vm.active.draw ? '.content.drawed' : '.content.typed')
						this.$refs.drawed.width = container.offsetWidth
						this.$refs.drawed.height = container.offsetHeight
						this.pad = new Pad(this.$refs.drawed)
						this.pad.addEventListener('endStroke', () => {
							vm.emptyPad = vm.pad.isEmpty();
						});
						window.addEventListener('resize', this._resize())
					} else {
						this.cancel()
						window.removeEventListener('resize', this._resize())
					}
				}
			})
		},
		isSigned: {
			immediate: true,
			handler(value) {
				this.element.value = value == true
				if (this.element.valid == false && value == true) {
					this.element.valid = true
				}
				store.commit('ADD_ELEMENT', this.element)
			}
		}
	},
	methods: {
		auth() {
			if (this.allowed) {
				store.commit('SET_ORIGINAL_MEMBER', store.state.members.find(m => m.active == true))
				store.commit('SET_MEMBERS', store.state.members.map(m => {
					m.active = this.allowed.type == m.type && this.allowed.code == m.code && this.allowed.email == m.email;
					return m
				}))
				store.dispatch('auth/start')
			}
		},
		classes(index) { return store.getters['classes'](index) },
		cancel() { this.typed = null; this.pad = null; this.active = { type: false, draw: false, allow: false }; this.emptyPad = true; },
		clear() { this.pad.clear(); this.emptyPad = true; },
		macro(value) { return store.getters['macro'](value) },
		options(value) { return store.getters['options'](this.definition, value) },
		trans(value) { let trans = store.getters.trans(value); return trans ? trans : this.$t('front.' + value) },
		remove() {
			if (store.state.type == 'preview-engagement' || store.state.complete == true || this.canUnSign == false || this.isLoading) {
				return null
			}
			store.dispatch('signature', {
				id: this.id,
				panel: this.element.panel,
				group: this.element.group,
				definition: this.definition.id,
			})
		},
		save() {
			if (store.state.type == 'preview-engagement' || this.isPreviewing || !this.canSign) {
				return null
			}
			let signature = null
			if (this.active.type && this.typed && this.typed.length > 0) {
				this._draw()
				signature = this._trim(this.$refs.typed).toDataURL()
			}
			if (this.active.draw && !this.pad.isEmpty()) {
				signature = this._trim(this.$refs.drawed).toDataURL()
			}
			if (signature != null) {
				let member = store.state.members.find(m => m.active == true)
				store.dispatch('signature', {
					id: this.id,
					panel: this.element.panel,
					group: this.element.group,
					definition: this.definition.id,
					value: signature,
					name: member.name,
					email: member.email,
				}).then(() => { this.$validate({ target: this.$refs.validSig }) })
			}
			this.typed = null
			this.active = { type: false, draw: false, allow: false }

		},
		toggle(id) {
			if (id == 'draw') { this.active = { draw: true, type: false, allow: false } }
			if (id == 'type') { this.active = { draw: false, type: true, allow: false } }
			if (id == 'allow') { this.active = { draw: false, type: false, allow: true } }
			if (this.active.type) { this.$nextTick(() => { if (this.$refs.ityped) this.$refs.ityped.focus() }) }
		},
		_draw: function () {
			let ctx = this.$refs.typed.getContext("2d")
			ctx.clearRect(0, 0, 300, 100)
			ctx.font = "50px Allura,cursive"
			ctx.textAlign = "center"
			ctx.textBaseline = "middle";
			ctx.fillText(this.typed, 150, 50, 300)
		},
		_resize: function () {
			if (this.pad == null || this.active.draw == false) {
				return
			}
			let ratio = Math.max(window.devicePixelRatio || 1, 1)
			this.$refs.drawed.width = this.$refs.drawed.offsetWidth * ratio
			this.$refs.drawed.height = this.$refs.drawed.offsetHeight * ratio
			this.$refs.drawed.getContext("2d").scale(ratio, ratio)
			this.pad.clear()
			this.emptyPad = true
		},
		_trim: function (canvas) {
			var ctx = canvas.getContext('2d'),
				copy = document.createElement('canvas').getContext('2d'),
				pixels = ctx.getImageData(0, 0, canvas.width, canvas.height),
				l = pixels.data.length,
				bound = { top: null, left: null, right: null, bottom: null },
				i, x, y

			for (i = 0; i < l; i += 4) {
				if (pixels.data[i + 3] !== 0) {
					x = (i / 4) % canvas.width
					y = ~~((i / 4) / canvas.width)
					if (bound.top === null) { bound.top = y }
					if (bound.left === null) { bound.left = x }
					else if (x < bound.left) { bound.left = x }
					if (bound.right === null) { bound.right = x }
					else if (bound.right < x) { bound.right = x }
					if (bound.bottom === null) { bound.bottom = y }
					else if (bound.bottom < y) { bound.bottom = y }
				}
			}
			var trimHeight = bound.bottom - bound.top,
				trimWidth = bound.right - bound.left,
				trimmed = ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight)
			copy.canvas.width = trimWidth
			copy.canvas.height = trimHeight
			copy.putImageData(trimmed, 0, 0)
			return copy.canvas
		}
	}
}
</script>
